<script setup lang="ts">
import { SwiperSlide } from 'swiper/vue';
import { Navigation, Grid } from 'swiper/modules';
import type { EventInfo } from '@/types/event';
import type { TranslatableEvent } from '@/types/translation';
import type { NavigationOptions, SwiperOptions } from 'swiper/types';

const swiperModules = [Navigation, Grid];

const ITEMS_PER_PAGE = {
	MOBILE: 4,
	DESKTOP: 8,
} as const;

const router = useRouter();
const searchStore = useSearchStore();
const profileStore = useProfileStore();
const { isMobile } = useWindowResize();
const { isTranslatedView, isTranslating, translate, setTranslatedView, trackTranslationSuccess } =
	useTranslation();

const translationTarget = {
	id: 'japan-events',
	location: 'japan',
	arrive: '',
	depart: '',
	tags: '',
	index: 0,
	name: { original: 'Japan Events', translated: null },
	description: { original: '', translated: null },
	imageUrl: '',
	slug: 'japan-events',
	coordinates: '0,0', // Coordinates as string to match TranslatableTarget type
	countryCode: 'JP', // Japan country code
} as const;

const translatedEvents = ref<TranslatableEvent[]>([]);
const isDisabledPrev = ref(false);
const isDisabledNext = ref(false);

// Type guard to check if an event is a TranslatableEvent
function isTranslatableEvent(event: any): event is TranslatableEvent {
	return (
		event &&
		typeof event === 'object' &&
		'name' in event &&
		typeof event.name === 'object' &&
		'original' in event.name
	);
}

// Convert a TranslatableEvent back to EventInfo for components that expect it
function toEventInfo(event: TranslatableEvent | EventInfo): EventInfo {
	if (isTranslatableEvent(event)) {
		const baseEvent: any = { ...event };
		baseEvent.name = event.name.original;
		if (event.groupName) {
			baseEvent.groupName = event.groupName.original;
		}
		if (event.address) {
			baseEvent.address = event.address.original;
		}
		return baseEvent as EventInfo;
	}
	return event;
}

const paginationRef = ref<{ handleClickNext: () => void }>();
const carouselRef = ref<{ handleSlideNext: () => void; handleSlidePrev: () => void }>();
const prevEl = ref<HTMLElement | null>(null);
const nextEl = ref<HTMLElement | null>(null);
const previousCurrentPage = ref<number | null>(null);
const currentPage = ref(searchStore.featuredEvents.pagination?.currentPage ?? 1);

const paginationInfo = computed(() => searchStore.featuredEvents.pagination);
const isLoading = computed(() => searchStore.featuredEvents.isLoading);
const isHomePage = computed(() => router.currentRoute.value.name === ROUTE.INDEX.NAME);
const itemsPerPage = computed(() =>
	isMobile.value ? ITEMS_PER_PAGE.MOBILE : ITEMS_PER_PAGE.DESKTOP,
);

// Use events from translation composable
const events = computed(() => translatedEvents.value.map(toEventInfo));

async function fetchEvents() {
	// if previous current page is equal to new current page, stop fetching
	if (currentPage.value === previousCurrentPage.value) {
		isDisabledNext.value = true;
		return;
	}

	try {
		// Get events from featured event list
		await searchStore.getFeaturedEvents({
			currentPage: currentPage.value,
			itemPerPage: itemsPerPage.value,
		});

		// Get events from the store after they're loaded
		const storeEvents = searchStore.featuredItems || [];

		// Convert events to translatable format
		const translatableEvents = storeEvents.map((event) => {
			const base: any = { ...event };

			// Ensure required fields are present
			base.name = { original: event.name, translated: null };

			// Handle optional fields
			if (event.groupName) {
				base.groupName = { original: event.groupName, translated: null };
			} else {
				delete base.groupName;
			}

			if (event.address) {
				base.address = { original: event.address, translated: null };
			} else {
				delete base.address;
			}

			return base as TranslatableEvent;
		});

		translatedEvents.value =
			currentPage.value === 1
				? [...translatableEvents]
				: [...translatedEvents.value, ...translatableEvents];

		previousCurrentPage.value = currentPage.value;
		trackTranslationSuccess(profileStore.profileId);
	} catch (error) {
		console.error('Failed to fetch events:', error);
	}

	// If translation mode is active, translate new events for this page
	if (isTranslatedView.value) {
		const toTranslate = collectTranslatableFields(
			translationTarget,
			translatedEvents.value,
			currentPage.value,
		);
		if (Object.keys(toTranslate).length) {
			const translations = await translate(toTranslate);
			applyTranslationsToEvents(
				translationTarget,
				translatedEvents.value,
				currentPage.value,
				translations,
			);
		}
		// Fire GTM translation success event only if API was used
		trackTranslationSuccess(profileStore.profileId);
	}
}

async function handleTranslateToggle() {
	if (isTranslatedView.value) {
		setTranslatedView(false);
		return;
	}

	// Collect fields to translate
	const toTranslate = collectTranslatableFields(
		translationTarget,
		translatedEvents.value,
		currentPage.value,
	);

	try {
		// Only translate if there are fields to translate
		if (Object.keys(toTranslate).length > 0) {
			const translations = await translate(toTranslate);

			// Apply translations
			applyTranslationsToEvents(
				translationTarget,
				translatedEvents.value,
				currentPage.value,
				translations,
			);

			// Update the events with translations
			setTranslatedView(true);
		}
	} catch (error) {
		console.error('Translation failed:', error);
	}
	// Fire GTM translation success event only if API was used
	trackTranslationSuccess(profileStore.profileId);
	setTranslatedView(true);
}

function handlePageChange(page: number) {
	currentPage.value = Number(page);
	fetchEvents();
}

function handleOnReachEnd() {
	if (isDisabledNext.value) {
		return;
	}
	paginationRef.value?.handleClickNext();
}

function handleClickNext() {
	if (isDisabledNext.value) {
		return;
	}
	carouselRef.value?.handleSlideNext();
}

function handleClickPrev() {
	if (isDisabledPrev.value) {
		return;
	}
	carouselRef.value?.handleSlidePrev();
}

function handleOnReachBeginning() {
	isDisabledPrev.value = true;
}

function handleOnSlideNext() {
	isDisabledPrev.value = false;
}

function handleOnSlidePrev() {
	isDisabledNext.value = false;
}

const swiperOptions: NavigationOptions & SwiperOptions = {
	slidesPerGroup: 2,
	freeMode: {
		enabled: true,
		sticky: false,
	},
	slidesPerView: 1.4,
	grid: {
		rows: 2,
		fill: 'row',
	},
	spaceBetween: 16,
	navigation: {
		prevEl: prevEl.value,
		nextEl: nextEl.value,
	},
	on: {
		reachEnd: handleOnReachEnd,
	},
	breakpoints: {
		768: {
			slidesPerGroup: 4,
			slidesPerView: 4,
			freeMode: false,
		},
	},
	grabCursor: true,
	a11y: {
		prevSlideMessage: 'Previous page',
		nextSlideMessage: 'Next page',
	},
};

watch(
	currentPage,
	() => {
		updatePaginationButtons();
	},
	{ immediate: true },
);

watch(
	paginationInfo,
	() => {
		updatePaginationButtons();
	},
	{ immediate: true },
);

function updatePaginationButtons() {
	const pagination = paginationInfo.value;
	if (!pagination || pagination.totalPages === 0) {
		// Don't update disabled states until we have valid pagination info
		return;
	}

	const newPage = currentPage.value;

	// Disable previous button only on first or second page
	isDisabledPrev.value = newPage <= 2;
	// Disable next button only on last page
	isDisabledNext.value = newPage >= pagination.totalPages + 1;
}

onBeforeUnmount(() => {
	searchStore.resetFeaturedEvents();
});

onMounted(() => {
	fetchEvents();
});
</script>

<template>
	<section class="japan-events-section">
		<div class="inner-section">
			<div class="japan-events-content">
				<h2 class="title text-semibold">What’s happening in Japan right now</h2>
				<p class="sub-title text-regular">Explore the Japan that most people never see.</p>
			</div>

			<div class="translation-container">
				<template v-if="isLoading">
					<ButtonSkeletonLoader width="206" height="42" />
				</template>
				<template v-else>
					<BaseButton
						v-if="translatedEvents?.length"
						class="translation-button"
						variant="outlined"
						color="gray"
						size="md"
						type="button"
						:disabled="isTranslating"
						@click="handleTranslateToggle"
					>
						<TranslateIcon :color="isTranslating ? ICON_COLOR.DISABLED : ICON_COLOR.DEFAULT" />
						{{ isTranslatedView ? 'Undo translation' : 'Translate this section' }}
					</BaseButton>
				</template>
			</div>

			<template v-if="translatedEvents?.length">
				<div class="event-carousel-container">
					<BaseCarousel
						ref="carouselRef"
						class="event-carousel"
						:swiper-options="swiperOptions"
						:modules="swiperModules"
						@on-to-edge="handleOnReachEnd"
						@on-reach-beginning="handleOnReachBeginning"
						@on-slide-next="handleOnSlideNext"
						@on-slide-prev="handleOnSlidePrev"
					>
						<SwiperSlide
							v-for="event in translatedEvents"
							:key="event.id"
							class="event-carousel-slide"
						>
							<EventCardItem :translatable-data="event" :is-translated="isTranslatedView" />
						</SwiperSlide>
					</BaseCarousel>
					<div
						ref="prevEl"
						:class="[
							'no-select pagination-left-icon',
							{
								'--disabled': isDisabledPrev,
							},
						]"
						@click="handleClickPrev"
					>
						<PaginationLeftIcon
							:color="isDisabledPrev ? ICON_COLOR.DISABLED : ICON_COLOR.DEFAULT"
						/>
					</div>
					<div
						ref="nextEl"
						:class="[
							'no-select pagination-right-icon',
							{
								'--disabled': isDisabledNext,
							},
						]"
						@click="handleClickNext"
					>
						<PaginationRightIcon
							:color="isDisabledNext ? ICON_COLOR.DISABLED : ICON_COLOR.DEFAULT"
						/>
					</div>
				</div>
			</template>
			<template v-else>
				<MultipleSkeletonLoader
					v-if="isLoading"
					class="event-carousel-skeleton-loader"
					:rows="isMobile ? 2 : 8"
				>
					<ImageSkeletonLoader height="280" />
				</MultipleSkeletonLoader>
				<NoDataFound v-else class="no-data-container">
					<template #content>
						<h1 class="text-md text-semibold">No events found</h1>
						<p class="text-sm text-regular">Please check back soon to find events in Japan.</p>
					</template>
				</NoDataFound>
			</template>
			<div class="paginate">
				<BasePagination
					v-if="events?.length"
					ref="paginationRef"
					:page="currentPage"
					:total-pages="paginationInfo?.totalPages"
					:is-loading="isLoading"
					@on-page-change="handlePageChange"
				/>
			</div>
			<div v-if="isHomePage" class="footer-container">
				<RouterLink :to="{ name: ROUTE.VISIT_JAPAN.NAME }">
					<BaseButton> Find Japanese events </BaseButton>
				</RouterLink>
			</div>
		</div>
	</section>
</template>

<style lang="scss" scoped>
.japan-events-section {
	min-height: rem(832);
	padding-top: spacings-get(12);
	text-align: center;
	background-color: colors-get(gray, 50);

	.inner-section {
		max-width: rem(1128);
		padding: 0 spacings-get(4);
		margin: 0 auto;
	}

	.japan-events-content {
		max-width: rem(1008);
		padding: 0 spacings-get(4);
		margin: 0 auto;

		> .title {
			color: colors-get(gray, 900);
			margin-bottom: spacings-get(5);
			@include fonts-size(display-md);

			@include media-query-max(mobile) {
				@include fonts-size(display-sm);
			}
		}

		> .sub-title {
			color: colors-get(gray, 600);
			padding-bottom: spacings-get(12);
			@include fonts-size(text-xl);

			@include media-query-max(mobile) {
				@include fonts-size(text-md);
			}
		}
	}

	.translation-container {
		margin: 0 rem(60) spacings-get(4);
		display: flex;
		justify-content: flex-start;

		@include media-query-max(mobile) {
			margin: 0 0 spacings-get(4);
		}
	}

	.event-carousel-container {
		position: relative;
		margin: 0 rem(60);

		@include media-query-max(mobile) {
			margin: 0;
		}

		.event-carousel {
			height: rem(576);
			padding: 0 0 spacings-get(2);
		}

		.pagination-left-icon,
		.pagination-right-icon {
			position: absolute;
			display: block;
			top: 50%;
			transform: translateY(-50%);
			cursor: pointer;

			@include media-query-max(mobile) {
				display: none;
			}

			&.--disabled {
				cursor: not-allowed;
			}
		}

		.pagination-left-icon {
			left: rem(-60);
		}

		.pagination-right-icon {
			right: rem(-60);
		}

		:deep(.swiper-button-prev),
		:deep(.swiper-button-next) {
			display: none;
		}
	}
}

.event-carousel-skeleton-loader {
	margin: 0 rem(60);
	display: grid !important;
	grid-template-columns: repeat(4, 1fr);
	grid-template-rows: repeat(2, 1fr);
	padding: 0 0 spacings-get(2);

	@include media-query-max(mobile) {
		margin: 0;
		grid-template-columns: repeat(1, 1fr);
		grid-template-rows: repeat(2, 1fr);
	}
}

.no-data-container {
	svg {
		margin: 0;
	}

	h1 {
		@include fonts-get(bold, 'text-xl');
	}

	p {
		@include fonts-get(regular, 'text-md');
	}
}

.paginate {
	display: none;
}

.footer-container {
	padding: spacings-get(12) 0;
}
</style>
